/**
 * email-render — generates the email HTML (and the plain-text alternative)
 * from an EmailDesign. This is the Outlook-proofing layer (operator decision
 * 10, 2026-07-30): nested tables, inline styles, MSO conditional wrappers,
 * VML bulletproof buttons, 600px fixed content width, bgcolor attributes.
 *
 * Modes:
 *  · standard — what modern clients get (embedded <style> for mobile stacking
 *    and, when theme.darkAware, prefers-color-scheme dark rules).
 *  · outlook  — emulates the Word engine for PREVIEW: no <style> block, no
 *    border-radius, the VML button path. The standard output already carries
 *    the MSO conditionals, so real Outlook gets this from the standard HTML;
 *    this mode exists so the preview can SHOW it.
 *  · dark     — dark palette applied directly, so the preview iframe can show
 *    dark mode without depending on the OS setting.
 */
import { type EmailBlock, type EmailDesign, emailFontStack, emailFontWebUrl } from "./email-model";

export type EmailRenderMode = "standard" | "outlook" | "dark";

const esc = (s: unknown) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const nl2br = (s: unknown) => esc(s).replace(/\n/g, "<br />");

interface Ctx { font: string; accent: string; text: string; soft: string; contentBg: string; mso: boolean; }

function blockHtml(b: EmailBlock, c: Ctx): string {
  const cfg = b.cfg as Record<string, string | number | boolean>;
  const align = String(cfg.align || "left");
  const pad = (v: string) => `padding:${v};`;
  switch (b.type) {
    case "logo": {
      const inner = cfg.imageUrl
        ? `<img src="${esc(cfg.imageUrl)}" alt="${esc(cfg.text || "Logo")}" height="32" style="height:32px;width:auto;border:0;display:inline-block;" />`
        : `<span style="font-family:${c.font};font-size:18px;font-weight:bold;letter-spacing:1px;color:${c.text};">${esc(cfg.text || "")}</span>`;
      return `<tr><td align="${align}" style="${pad("24px 32px 8px")}text-align:${align};">${inner}</td></tr>`;
    }
    case "heading": {
      const size = cfg.size === "md" ? 20 : 26;
      return `<tr><td align="${align}" class="em-text" style="${pad("16px 32px 6px")}font-family:${c.font};font-size:${size}px;line-height:1.3;font-weight:bold;color:${c.text};text-align:${align};">${nl2br(cfg.text)}</td></tr>`;
    }
    case "text":
      return `<tr><td align="${align}" class="em-text" style="${pad("8px 32px")}font-family:${c.font};font-size:14px;line-height:1.6;color:${c.text};text-align:${align};">${nl2br(cfg.text)}</td></tr>`;
    case "button": {
      const label = esc(cfg.label || "Open");
      /* Target is a typed URL OR a catalog link token (CD prompt 2026-09-01 §4).
         A token target emits the PLACEHOLDER as the href — the platform resolves
         it per recipient, so nothing here needs to know the recipient. */
      const url = esc(cfg.target === "token" && cfg.token ? `{{${cfg.token}}}` : (cfg.url || "#"));
      const w = Math.max(120, Math.min(280, String(cfg.label || "Open").length * 9 + 64));
      // Outlook PREVIEW stand-in. The real Outlook path is the VML below, but no
      // browser renders <v:roundrect> — emitting it into the preview iframe made
      // the button disappear entirely (operator, 2026-07-31). Draw what the VML
      // actually produces instead: solid rounded fill, 44px tall, white bold
      // label, fixed width — so the emulation shows Outlook's real result.
      if (c.mso) {
        return `<tr><td align="${align}" style="${pad("14px 32px")}text-align:${align};"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${align}" style="display:inline-table;"><tr><td bgcolor="${c.accent}" align="center" height="44" width="${w}" style="background:${c.accent};height:44px;width:${w}px;border-radius:6px;font-family:${c.font};font-size:14px;font-weight:bold;color:#ffffff;text-align:center;">${label}</td></tr></table></td></tr>`;
      }
      const vml = `<!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${url}" style="height:44px;v-text-anchor:middle;width:${w}px;" arcsize="14%" strokecolor="${c.accent}" fillcolor="${c.accent}"><w:anchorlock/><center style="color:#ffffff;font-family:Arial,sans-serif;font-size:14px;font-weight:bold;">${label}</center></v:roundrect><![endif]-->`;
      const a = `<a href="${url}" style="display:inline-block;background:${c.accent};color:#ffffff;font-family:${c.font};font-size:14px;font-weight:bold;line-height:44px;text-align:center;text-decoration:none;padding:0 28px;border-radius:6px;mso-hide:all;">${label}</a>`;
      return `<tr><td align="${align}" style="${pad("14px 32px")}text-align:${align};">${vml}<!--[if !mso]><!-- -->${a}<!--<![endif]--></td></tr>`;
    }
    case "image": {
      if (!cfg.url) return `<tr><td style="${pad("8px 32px")}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" bgcolor="#EDEDF2" style="background:#EDEDF2;${pad("42px 12px")}font-family:${c.font};font-size:12px;color:#697180;">${esc(cfg.alt || "Image")}</td></tr></table></td></tr>`;
      const radius = cfg.radius && !c.mso ? "border-radius:8px;" : "";
      return `<tr><td style="${pad("8px 32px")}"><img src="${esc(cfg.url)}" alt="${esc(cfg.alt || "")}" width="536" style="width:100%;max-width:536px;height:auto;display:block;border:0;${radius}" /></td></tr>`;
    }
    case "columns": {
      const cell = (title: unknown, text: unknown, padSide: string) =>
        `<td class="stack" width="50%" valign="top" style="width:50%;${pad(`4px ${padSide}`)}"><div style="font-family:${c.font};font-size:14px;font-weight:bold;color:${c.text};padding-bottom:4px;" class="em-text">${nl2br(title)}</div><div class="em-text" style="font-family:${c.font};font-size:13px;line-height:1.55;color:${c.text};">${nl2br(text)}</div></td>`;
      return `<tr><td style="${pad("8px 32px")}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>${cell(cfg.leftTitle, cfg.leftText, "12px 0 0")}${cell(cfg.rightTitle, cfg.rightText, "0 0 12px")}</tr></table></td></tr>`;
    }
    case "calendar_link": {
      /* The calendar targets side by side — that is what makes this a block rather
         than a Button pointing at one calendar token. Plain anchors, not VML.
         No Google link (CC 0362): appointment emails carry the invitation itself, and
         a provider link's entry could never be updated or removed. A stored block
         with google: true renders without it. */
      const on: [boolean, string, string][] = [
        [cfg.ics !== false, "{{calendar_ics_url}}", "Calendar (.ics)"],
        [cfg.apple !== false, "{{calendar_apple_url}}", "Apple"],
      ];
      const links = on.filter(([o]) => o)
        .map(([, href, name]) => `<a href="${href}" style="color:${c.accent};font-family:${c.font};font-size:13px;font-weight:bold;text-decoration:underline;">${esc(name)}</a>`)
        .join(`<span style="color:${c.soft};font-family:${c.font};font-size:13px;">&nbsp;&nbsp;·&nbsp;&nbsp;</span>`);
      if (!links) return "";
      return `<tr><td align="${align}" style="${pad("10px 32px")}text-align:${align};font-family:${c.font};font-size:13px;color:${c.text};">${cfg.label ? `<div style="padding-bottom:6px;">${esc(cfg.label)}</div>` : ""}${links}</td></tr>`;
    }
    case "qr":
    case "attendee_qr": {
      /* A server-rendered image, never inline SVG: Outlook's Word engine does
         not draw SVG, and a QR that fails to render is a guest at a locked
         door. `attendee_qr` repeats per attendee at send time — one row here is
         the authored TEMPLATE for that repetition, which is why the caption is
         singular copy and the names flag lives on the block. */
      const size = Math.max(96, Math.min(240, Number(cfg.size) || (b.type === "qr" ? 160 : 128)));
      const src = b.type === "qr" ? "{{recipient.credential_qr_svg_url}}" : "{{attendee.credential_qr_svg_url}}";
      const alt = b.type === "qr" ? "Your check-in code" : "Attendee check-in code";
      const nameLine = b.type === "attendee_qr" && cfg.showNames !== false
        ? `<div style="font-family:${c.font};font-size:12px;font-weight:bold;color:${c.text};padding-bottom:5px;">{{attendee.full_name}}</div>` : "";
      const caption = cfg.caption
        ? `<div style="font-family:${c.font};font-size:12px;line-height:1.5;color:${c.soft};padding-top:7px;">${nl2br(cfg.caption)}</div>` : "";
      return `<tr><td align="${align}" style="${pad("12px 32px")}text-align:${align};">${nameLine}<img src="${src}" alt="${esc(alt)}" width="${size}" height="${size}" style="width:${size}px;height:${size}px;display:inline-block;border:0;" />${caption}</td></tr>`;
    }
    case "countdown": {
      /* An IMAGE, not live CSS: nothing animates in Outlook, and a countdown
         frozen at "00" in the client with the largest share is worse than an
         honest server-rendered frame. */
      return `<tr><td align="${align}" style="${pad("12px 32px")}text-align:${align};">${cfg.label ? `<div style="font-family:${c.font};font-size:13px;color:${c.soft};padding-bottom:6px;">${esc(cfg.label)}</div>` : ""}<img src="{{event.countdown_image_url}}" alt="${esc(String(cfg.label || "Countdown"))}" width="280" style="width:280px;max-width:100%;height:auto;display:inline-block;border:0;" /></td></tr>`;
    }
    case "divider":
      return `<tr><td style="${pad("14px 32px")}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="border-top:1px solid #E6E8EC;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr>`;
    case "spacer":
      return `<tr><td height="${Number(cfg.height) || 24}" style="height:${Number(cfg.height) || 24}px;font-size:0;line-height:0;">&nbsp;</td></tr>`;
    case "footer":
      return `<tr><td align="center" style="${pad("18px 32px 26px")}font-family:${c.font};font-size:12px;line-height:1.6;color:${c.soft};">${nl2br(cfg.note)}<br />${nl2br(cfg.address)}<br /><a href="{{unsubscribe_url}}" style="color:${c.soft};text-decoration:underline;">${esc(cfg.unsubscribeLabel || "Unsubscribe")}</a></td></tr>`;
    case "code":
      // Verbatim by design (the Custom HTML block / reading 2): the user owns
      // this fragment's client compatibility, and the inspector says so.
      return `<tr><td style="${pad("8px 32px")}">${String(cfg.html || "")}</td></tr>`;
    default:
      return "";
  }
}

export function renderEmailHtml(design: EmailDesign, opts?: { lang?: string; mode?: EmailRenderMode }): string {
  const mode: EmailRenderMode = opts?.mode || "standard";
  const mso = mode === "outlook";
  const dark = mode === "dark";
  const t = design.theme;
  const bg = dark ? "#1B1B1F" : t.bg;
  const contentBg = dark ? "#26262B" : t.contentBg;
  const text = dark ? "#E6E8EC" : t.text;
  const soft = dark ? "#9AA0AC" : "#697180";
  const c: Ctx = { font: emailFontStack(t.font), accent: t.accent, text, soft, contentBg, mso };
  const rows = design.blocks.map((b) => blockHtml(b, c)).join("\n");
  /* A brand font needs an @font-face link. Outlook desktop and Gmail ignore it and
     use the stack's fallback — that is the documented trade, not a bug. Never
     emitted in outlook mode, where it would only be noise. */
  const webUrl = emailFontWebUrl(t.font);
  const fontLink = webUrl && !mso ? `<link href="${webUrl}" rel="stylesheet" />` : "";
  const style = mso ? "" : `<style>
  @media only screen and (max-width:620px){ .em-main{width:100% !important;} .stack{display:block !important;width:100% !important;padding:4px 0 !important;} }
  ${t.darkAware && !dark ? `@media (prefers-color-scheme: dark){ .em-body{background:#1B1B1F !important;} .em-card{background:#26262B !important;} .em-text{color:#E6E8EC !important;} }` : ""}
</style>`;
  return `<!DOCTYPE html>
<html lang="${esc(opts?.lang || "en")}" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<meta name="color-scheme" content="light dark" /><meta name="supported-color-schemes" content="light dark" />
${fontLink}
<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
${style}
</head>
<body class="em-body" bgcolor="${bg}" style="margin:0;padding:0;background:${bg};-webkit-text-size-adjust:100%;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" bgcolor="${bg}" style="background:${bg};"><tr><td align="center" style="padding:24px 12px;">
<!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0"><tr><td><![endif]-->
<table role="presentation" class="em-main em-card" width="600" cellpadding="0" cellspacing="0" bgcolor="${contentBg}" style="width:600px;max-width:600px;background:${contentBg};${mso ? "" : "border-radius:10px;"}">
${rows}
</table>
<!--[if mso]></td></tr></table><![endif]-->
</td></tr></table>
</body></html>`;
}

/** Plain-text alternative — generated automatically (operator decision 11) and
 *  written back as the language's email body so completeness + previews hold. */
export function renderEmailPlainText(design: EmailDesign): string {
  const lines: string[] = [];
  design.blocks.forEach((b) => {
    const cfg = b.cfg as Record<string, string | number>;
    switch (b.type) {
      case "logo": if (cfg.text) lines.push(String(cfg.text), ""); break;
      case "heading": lines.push(String(cfg.text || "").toUpperCase(), ""); break;
      case "text": lines.push(String(cfg.text || ""), ""); break;
      case "button": lines.push(`${cfg.label || "Open"}: ${cfg.target === "token" && cfg.token ? `{{${cfg.token}}}` : (cfg.url || "")}`, ""); break;
      case "image": if (cfg.alt) lines.push(`[${cfg.alt}]`, ""); break;
      case "columns": lines.push(`${cfg.leftTitle || ""} — ${cfg.leftText || ""}`, `${cfg.rightTitle || ""} — ${cfg.rightText || ""}`, ""); break;
      case "code": lines.push(stripHtmlToText(String(cfg.html || "")), ""); break;
      /* The plain-text alternative carries the same artefacts — a text-only
         client is exactly where a QR image is useless and its URL is not. */
      case "calendar_link": lines.push(String(cfg.label || "Add to your calendar"), "{{calendar_ics_url}}", ""); break;
      case "qr": lines.push(String(cfg.caption || "Your check-in code"), "{{recipient.credential_qr_svg_url}}", ""); break;
      case "attendee_qr": lines.push(String(cfg.caption || "Attendee check-in codes"), "{{attendee.full_name}}: {{attendee.credential_qr_svg_url}}", ""); break;
      case "countdown": lines.push(String(cfg.label || "Starts in"), ""); break;
      case "divider": lines.push("----------------------------------------", ""); break;
      case "footer": lines.push(String(cfg.note || ""), String(cfg.address || ""), `${cfg.unsubscribeLabel || "Unsubscribe"}: {{unsubscribe_url}}`); break;
      default: break;
    }
  });
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** Data-URL images can't survive a real send — Gmail and Outlook strip them, and
 *  they inflate the message toward Gmail's 102KB clip. Dropped files become data
 *  URLs so the design is immediately visible; the UI warns until they're hosted
 *  (REQ-112i). */
export const isDataUrl = (u?: string | null) => !!u && /^data:/i.test(u);

export const emailSizeKb = (html: string) => Math.round((new Blob([html]).size / 1024) * 10) / 10;
/** Gmail clips messages over ~102KB — surfaced as a preview warning. */
export const GMAIL_CLIP_KB = 102;

/** Crude but serviceable text extraction for CUSTOM HTML emails (step 3) — the
 *  generated plain-text alternative for designed emails comes from the design
 *  itself; this is only for code-sourced messages and code blocks. */
export function stripHtmlToText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|td|tr|table|h[1-6]|li|section)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"')
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
